/**
 * Runtime Extraction Adapter — 3D Tiles with Feature Metadata
 *
 * P3 — INIT-2026-Q3-SCENE-HIERARCHY (runtime extraction adapters).
 * Builds a USN hierarchy from batch-table properties when the tileset
 * carries EXT_mesh_features / EXT_structural_metadata or legacy batch
 * table data.
 *
 * Hierarchy: Project → Tile → Feature nodes built from batch-table rows.
 *
 * Detect: layerKind === "3dtiles" AND no existing converter manifest.
 */

import type { SceneItem } from "@/sdk-stub/gtwin-sdk/types";
import type {
  TreeNode,
  SceneHierarchyManifest,
  TreeNodeDomain,
} from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";
import type {
  DomainAdapter,
  DomainAdapterContext,
  AdapterResult,
} from "../DomainAdapter";

const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

function generateUlid(): string {
  let t = Date.now();
  const tsChars: string[] = [];
  for (let i = 0; i < 10; i++) {
    tsChars.unshift(CROCKFORD[t % 32]);
    t = Math.floor(t / 32);
  }
  const randBytes = new Uint8Array(10);
  crypto.getRandomValues(randBytes);
  let rand = "";
  for (let i = 0; i < 10; i++) {
    rand += CROCKFORD[randBytes[i] & 0x1f];
    rand += CROCKFORD[(randBytes[i] >> 5) & 0x07 | ((i < 9 ? randBytes[i + 1] & 0x03 : 0) << 3)];
  }
  return tsChars.join("") + rand.slice(0, 16);
}

interface BatchTableRow {
  featureId: number | string;
  parentId?: string | null;
  level?: number;
  kind?: string;
  label?: string;
  [key: string]: unknown;
}

function usnMeta(id: string, parentId: string | null) {
  return {
    objectId: id,
    aspects: {
      spatial: { parentId },
      functional: { parentId },
    },
    relationships: [] as const,
  };
}

function buildFromBatchTable(
  item: SceneItem,
  asset: { layerId: string; assetNodeId: string; gtwinId: string },
  rows: BatchTableRow[],
): AdapterResult {
  const nodes: TreeNode[] = [];
  const rootId = generateUlid();

  const root: TreeNode = {
    id: rootId,
    parentId: null,
    lor: 3,
    label: asset.assetNodeId || asset.layerId,
    kind: "asset",
    layerId: asset.layerId,
    featureId: null,
    domain: "smartcity" as TreeNodeDomain,
    standardRef: null,
    metadata: { runtimeExtracted: true, source: "3dtiles-batch-table" },
    hasChildren: rows.length > 0,
    sortKey: 0,
    ...usnMeta(rootId, null),
  };
  nodes.push(root);

  const idMap = new Map<string | number, string>();

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const nodeId = generateUlid();
    idMap.set(row.featureId, nodeId);

    const effectiveParentId =
      row.parentId && idMap.has(row.parentId)
        ? idMap.get(row.parentId)!
        : rootId;

    const node: TreeNode = {
      id: nodeId,
      parentId: effectiveParentId,
      lor: row.level != null ? Math.min(row.level + 3, 8) : 6,
      label: row.label ?? `Feature ${row.featureId}`,
      kind: (row.kind as TreeNode["kind"]) ?? "element",
      layerId: asset.layerId,
      featureId: `feature:${row.featureId}`,
      domain: "smartcity" as TreeNodeDomain,
      standardRef: null,
      metadata: { ...row, runtimeExtracted: true },
      hasChildren: false,
      sortKey: i,
      ...usnMeta(nodeId, effectiveParentId),
    };
    nodes.push(node);
  }

  const manifest: SceneHierarchyManifest = {
    schema: "geosoft.scene-hierarchy/1",
    gtwinId: asset.gtwinId,
    rootLayerId: asset.layerId,
    generatedAt: new Date().toISOString(),
    domain: "smartcity",
    standardName: "3DTiles",
    standardVersion: "1.1",
    totalNodes: nodes.length,
    nodeCountByLoR: {},
    nodes,
    paginated: false,
    dataSources: {
      [asset.layerId]: {
        layerId: asset.layerId,
        label: "3D Tiles",
        mediaKind: item.layerKind,
        primary: true,
      },
    },
  };

  return { ok: true, manifest };
}

export const TilesFeatureAdapter: DomainAdapter = {
  domain: "smartcity" as TreeNodeDomain,
  version: "1.0.0",
  standard: "3DTiles:1.1",

  detect(item: SceneItem): boolean {
    return item.layerKind === "3dtiles";
  },

  async buildManifest(
    item: SceneItem,
    asset: { layerId: string; assetNodeId: string; gtwinId: string },
    context: DomainAdapterContext,
  ): Promise<AdapterResult> {
    const href =
      item.endpoints["href"] ?? Object.values(item.endpoints)[0] ?? "";

    if (context.fetchJson) {
      try {
        const raw = await context.fetchJson(`${href}/batch-table.json`);
        if (Array.isArray(raw)) {
          return buildFromBatchTable(item, asset, raw as BatchTableRow[]);
        }
      } catch {
        // Fall through to synthetic
      }
    }

    return buildFromBatchTable(item, asset, []);
  },
};
